import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/vehicleSalesorScrapReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();
const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};
const fmtMoney = (v) =>
  toNum(v, 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getQS = (search) => {
  const p = new URLSearchParams(search);
  const obj = {};
  for (const [k, v] of p.entries()) obj[k] = v;
  return obj;
};

export default function VehicleSalesorScrapReport_Print() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ✅ filters from URL (same as your first code)
  const filters = useMemo(() => {
    const q = getQS(location.search);
    return {
      fromDate: toStr(q.fromDate),
      toDate: toStr(q.toDate),
      branchId: toStr(q.branchId) || "Select",
      regNo: toStr(q.regNo),
      vehicleCode: toStr(q.vehicleCode),
      vehicleName: toStr(q.vehicleName),
      status: toStr(q.status) || "All",
      vehicleTypeId: toStr(q.vehicleTypeId) || "Select",
    };
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rawRows, setRawRows] = useState([]);

  // ✅ Builds print url
  const buildPrintUrl = () => {
    const params = new URLSearchParams();
    params.set("fromDate", filters.fromDate);
    params.set("toDate", filters.toDate);
    params.set("branchId", filters.branchId);
    params.set("regNo", filters.regNo);
    params.set("vehicleCode", filters.vehicleCode);
    params.set("vehicleName", filters.vehicleName);
    params.set("status", filters.status);
    params.set("vehicleTypeId", filters.vehicleTypeId);
    return `${API_BASE}/print?${params.toString()}`;
  };

  // ✅ fetch
  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setErr("");
        const url = buildPrintUrl();
        const { data } = await axios.get(url);
        if (!data?.success) throw new Error(data?.message || "Print fetch failed");
        if (alive) setRawRows(data.rows || []);
      } catch (e) {
        if (alive) {
          setRawRows([]);
          setErr(e?.response?.data?.message || e.message || "Print fetch failed");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ normalize for table (same mapping as your first code)
  const rows = useMemo(() => {
    const pick = (r, keys, d = "") => {
      for (const k of keys) {
        const v = r?.[k];
        if (v !== undefined && v !== null && String(v).trim() !== "") return v;
      }
      return d;
    };

    const getPrice = (r) =>
      pick(r, ["fld_soldprice", "fld_price", "SoldPrice", "soldPrice"], 0);

    return (rawRows || []).map((r, idx) => ({
      sno: idx + 1,
      mainType: pick(r, ["VehicleType", "vehiclemaintype", "fld_vehiclemaintypename", "mainType"], ""),
      vehicleCode: pick(r, ["VehicleCode", "vehiclecode", "fld_vehicle_code", "fld_vehiclecode"], ""),
      vehicleName: pick(r, ["VehicleName", "vehiclename", "fld_vehicle_name", "name"], ""),
      regNo: pick(r, ["VehicleRegNo", "vehicleregno", "fld_vehicle_regno", "regNo"], ""),
      branch: pick(r, ["BranchName", "branchname", "fld_BranchName", "Branch"], ""),
      status: pick(r, ["fld_sold_sales", "status", "fld_status"], ""),
      soldPrice: getPrice(r),
      reason: pick(r, ["fld_remark", "reason", "fld_reason", "fld_remarks"], ""),
      recommendedBy: pick(r, ["RecommendedBy", "recommendby", "fld_recommendby", "RecommendBy"], ""),
      approvedBy: pick(r, ["ApprovedBy", "approvedby", "fld_approvedby"], ""),
      _row: r,
    }));
  }, [rawRows]);

  const headerFiltersText = useMemo(() => {
    const parts = [];
    if (filters.fromDate) parts.push(`From: ${filters.fromDate}`);
    if (filters.toDate) parts.push(`To: ${filters.toDate}`);
    if (filters.branchId && filters.branchId !== "Select") parts.push(`Branch: ${filters.branchId}`);
    if (filters.regNo) parts.push(`Reg.No: ${filters.regNo}`);
    if (filters.vehicleCode) parts.push(`Code: ${filters.vehicleCode}`);
    if (filters.vehicleName) parts.push(`Name: ${filters.vehicleName}`);
    if (filters.status && filters.status !== "All") parts.push(`Status: ${filters.status}`);
    if (filters.vehicleTypeId && filters.vehicleTypeId !== "Select") parts.push(`MainType: ${filters.vehicleTypeId}`);
    return parts.join(" | ");
  }, [filters]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* ✅ print CSS (same style approach like your Tyre print template) */}
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          body { margin: 0; padding: 0; font-size: 9pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; }
          th, td { border: 1px solid #000 !important; }
        }
      `}</style>

      <div className="p-3">
        {/* Top actions (not printed) */}
        <div className="no-print flex items-center justify-between mb-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded hover:bg-gray-50"
          >
            ← Back
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => window.print()}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded shadow"
            >
              🖨️ Print
            </button>
          </div>
        </div>

        {/* Loading / Error (not printed) */}
        {loading ? (
          <div className="no-print p-3 border rounded bg-gray-50 text-sm mb-2">
            Loading print data...
          </div>
        ) : err ? (
          <div className="no-print p-3 border rounded bg-red-50 text-sm text-red-700 mb-2">
            {err}
          </div>
        ) : null}

        {/* Header block */}
        <div className="border-2 border-black mb-2">
          <div className="flex items-center p-2">
            {/* Logo */}
            <div className="w-24 h-14 border border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
              <div className="text-center leading-tight">
                <div className="text-xs">NISSI</div>
                <div className="text-xs">INFOTECH</div>
                <div className="text-xs">PVT LTD</div>
              </div>
            </div>

            <div className="flex-1 text-center">
              <div className="text-lg font-bold">Nissi Infotech</div>
              <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
              <div className="text-xs">
                3/42-12, Nagappa Gounder, Kavundampalayam, Coimbatore - 641030 - Tamil Nadu.
              </div>
              <div className="text-xs">Phone No : 9003744644</div>
            </div>

            <div className="text-xs text-right w-28">
              <div>INFRA SCREEN</div>
              <div className="font-semibold">B-56</div>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-2 border-2 border-black py-1">
          <div className="text-base font-bold">VEHICLE SALE OR SCRAP REPORT</div>
          <div className="text-xs">{headerFiltersText || " "}</div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-black text-xs">
            <thead>
              <tr className="bg-gray-200">
                <th className="px-1 py-1 font-bold text-center">S.No</th>
                <th className="px-1 py-1 font-bold text-center">Vehicle Main type</th>
                <th className="px-1 py-1 font-bold text-center">Vehicle Code</th>
                <th className="px-1 py-1 font-bold text-center">Vehicle Name</th>
                <th className="px-1 py-1 font-bold text-center">Reg No.</th>
                <th className="px-1 py-1 font-bold text-center">Branch</th>
                <th className="px-1 py-1 font-bold text-center">Status</th>
                <th className="px-1 py-1 font-bold text-center">Sold Price</th>
                <th className="px-1 py-1 font-bold text-center">Reason for Sale / Scrap</th>
                <th className="px-1 py-1 font-bold text-center">Recommend by</th>
                <th className="px-1 py-1 font-bold text-center">Approved by</th>
              </tr>
            </thead>

            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-2 py-2 text-center">
                    No Records
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.sno}>
                    <td className="px-1 py-1 text-center">{r.sno}</td>
                    <td className="px-1 py-1">{r.mainType}</td>
                    <td className="px-1 py-1 text-center">{r.vehicleCode}</td>
                    <td className="px-1 py-1">{r.vehicleName}</td>
                    <td className="px-1 py-1 text-center">{r.regNo}</td>
                    <td className="px-1 py-1">{r.branch}</td>
                    <td className="px-1 py-1 text-center">{r.status}</td>
                    <td className="px-1 py-1 text-right">{fmtMoney(r.soldPrice)}</td>
                    <td className="px-1 py-1">{r.reason}</td>
                    <td className="px-1 py-1">{r.recommendedBy}</td>
                    <td className="px-1 py-1">{r.approvedBy}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="mt-2 text-xs border-2 border-black p-1">
          Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com
        </div>
      </div>
    </div>
  );
}
